import React, { useEffect, useState } from 'react';
import { ExternalLink, Copy, Check, Undo2, Gift, Loader2 } from 'lucide-react';
import { getAuthHeaders } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface Reward {
  milestone: number;
  code: string | null;
  status: 'granted' | 'redeemed' | 'owed' | 'suppressed';
  used: boolean;
  earnedAt: string;
  monthsAtEarn: number;
}

interface RewardsData {
  eligible: boolean;
  available: boolean;   // a reward is earned and waiting to be claimed
  poolEmpty: boolean;   // earned, but we're out of codes this moment
  rewards: Reward[];
  redeemUrl: string;
  rewardValue: string;
  intervalMonths: number;
  membershipMonths: number;
  nextRewardInMonths: number | null;
  nextRewardDate: string | null;
}

const TERMS_URL = 'https://www.ticketrewards.com/terms';

// Loyalty perk: a $25 Ticket Rewards code every few months of HJ+ membership.
// Tan "paper" card to match the warm brand surfaces. Self-hides for non-members.
export const TicketRewards: React.FC = () => {
  const { toast } = useToast();
  const [data, setData] = useState<RewardsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    fetch('/api/ticket-rewards', { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;

  const granted = data.rewards.filter((r) => ['granted', 'redeemed'].includes(r.status) && r.code);
  const owed = data.rewards.filter((r) => r.status === 'owed').length;
  if (!data.eligible && granted.length === 0 && owed === 0) return null;

  // Claiming is what takes a code out of the pool — nothing is minted on a timer,
  // so this button is the only way a reward ever gets spent.
  const claim = async () => {
    setClaiming(true);
    try {
      const r = await fetch('/api/ticket-rewards/redeem', { method: 'POST', headers: getAuthHeaders() });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast({
          title: body.code === 'POOL_EMPTY' ? 'We just ran out' : 'Couldn\u2019t claim it',
          description: body.code === 'POOL_EMPTY'
            ? 'You keep your reward — try again in a day or so and it\u2019ll be here.'
            : body.error || 'Try again in a moment.',
          variant: 'destructive',
        });
        return;
      }
      const fresh = await fetch('/api/ticket-rewards', { headers: getAuthHeaders() }).then((x) => (x.ok ? x.json() : null));
      if (fresh) setData(fresh);
      toast({ title: `Your ${data.rewardValue} code is ready`, description: 'We emailed it to you too.' });
    } catch {
      toast({ title: 'Couldn\u2019t claim it', description: 'Try again in a moment.', variant: 'destructive' });
    } finally { setClaiming(false); }
  };

  const months = (n: number | null) => (n === 1 ? '1 month' : `${n} months`);
  const copy = (code: string) => {
    navigator.clipboard.writeText(code);
    toast({ title: 'Code copied' });
  };

  // Manual, reversible "used" flag — Ticket Rewards doesn't tell us when a code is
  // burned, so members mark it themselves. Optimistic update; undo just re-toggles.
  const setUsed = async (milestone: number, used: boolean) => {
    setBusy(milestone);
    try {
      const r = await fetch('/api/ticket-rewards/mark-used', {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ milestone, used }),
      });
      if (!r.ok) throw new Error('request failed');
      setData((prev) =>
        prev
          ? { ...prev, rewards: prev.rewards.map((rw) => (rw.milestone === milestone ? { ...rw, used } : rw)) }
          : prev,
      );
      toast({ title: used ? 'Marked as used' : 'Marked as unused' });
    } catch {
      toast({ title: "Couldn't update — please try again" });
    } finally {
      setBusy(null);
    }
  };

  const redeemBtn: React.CSSProperties = {
    padding: '8px 16px', borderRadius: 999, background: 'var(--hj-blue)',
    color: 'oklch(0.18 0.005 70)', fontSize: 12.5, fontWeight: 700,
    textDecoration: 'none', whiteSpace: 'nowrap', flexShrink: 0,
  };
  const copyBtn: React.CSSProperties = {
    width: 34, height: 34, borderRadius: 999, background: 'transparent',
    border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  };
  const toggleUsedBtn: React.CSSProperties = {
    marginTop: 8, padding: 0, background: 'transparent', border: 'none',
    color: 'var(--hj-beige-ink-3)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
    textDecoration: 'underline', textUnderlineOffset: 2,
    display: 'inline-flex', alignItems: 'center', gap: 4,
  };
  const stepNum: React.CSSProperties = {
    width: 20, height: 20, borderRadius: 999, background: 'var(--hj-blue)',
    color: 'oklch(0.18 0.005 70)', fontSize: 11, fontWeight: 800, flexShrink: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--hj-font-mono)',
  };

  return (
    <section
      data-paper="beige"
      style={{
        padding: 24,
        borderRadius: 'var(--hj-r-md)',
        background: 'var(--hj-beige)',
        border: '1px solid var(--hj-beige-line)',
        color: 'var(--hj-beige-ink)',
      }}
    >
      <img src="/brand/ticket-rewards-logo.png" alt="Ticket Rewards" style={{ height: 24, width: 'auto', display: 'block' }} />
      <p className="m-0" style={{ marginTop: 12, fontSize: 13.5, color: 'var(--hj-beige-ink-2)', lineHeight: 1.5 }}>
        A {data.rewardValue} thank-you for sticking with Hey Joe Plus — claim one every {data.intervalMonths} months.{' '}
        {data.available
          ? 'You\u2019ve got one waiting.'
          : data.nextRewardDate
            ? `Your next code unlocks ${new Date(data.nextRewardDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}.`
            : data.nextRewardInMonths != null
              ? `Your next code unlocks in ${months(data.nextRewardInMonths)}.`
              : ''}
      </p>

      {data.available && (
        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            onClick={claim}
            disabled={claiming}
            className="inline-flex items-center gap-2"
            style={{
              padding: '12px 20px', borderRadius: 'var(--hj-r-sm)', border: 0,
              background: 'var(--hj-beige-ink)', color: 'var(--hj-beige)',
              fontSize: 14, fontWeight: 700, cursor: claiming ? 'default' : 'pointer',
              opacity: claiming ? 0.7 : 1, minHeight: 44,
            }}
          >
            {claiming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
            {claiming ? 'Getting your code…' : `Claim my ${data.rewardValue} code`}
          </button>
          <p className="m-0" style={{ marginTop: 8, fontSize: 12, color: 'var(--hj-beige-ink-3)' }}>
            Your next one starts counting from the day you claim this.
          </p>
        </div>
      )}

      {granted.length > 0 && (
        <div className="flex flex-col gap-2" style={{ marginTop: 16 }}>
          {granted.map((r) => (
            <div
              key={r.milestone}
              className="flex items-center gap-3"
              style={{ padding: 14, borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-beige-deep)', border: '1px solid var(--hj-beige-line)', opacity: r.used ? 0.6 : 1 }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="mono-label" style={{ fontSize: 9, color: 'var(--hj-beige-ink-3)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  {data.rewardValue} code
                  {r.used && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--hj-beige-ink-2)' }}>
                      <Check size={11} /> used
                    </span>
                  )}
                </div>
                <div
                  className="tabular"
                  style={{ fontSize: 19, fontWeight: 700, color: 'var(--hj-beige-ink)', fontFamily: 'var(--hj-font-mono)', letterSpacing: 1.5, wordBreak: 'break-all', textDecoration: r.used ? 'line-through' : 'none' }}
                >
                  {r.code}
                </div>
                <button
                  type="button"
                  onClick={() => setUsed(r.milestone, !r.used)}
                  disabled={busy === r.milestone}
                  style={{ ...toggleUsedBtn, opacity: busy === r.milestone ? 0.5 : 1 }}
                >
                  {r.used ? (<><Undo2 size={12} /> Mark as unused</>) : 'Mark as used'}
                </button>
              </div>
              {!r.used && (
                <>
                  <button type="button" onClick={() => copy(r.code!)} aria-label="Copy code" style={copyBtn}>
                    <Copy size={16} />
                  </button>
                  <a href={data.redeemUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1" style={redeemBtn}>
                    Redeem <ExternalLink size={13} />
                  </a>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {owed > 0 && (
        <p className="m-0" style={{ marginTop: 12, fontSize: 12.5, color: 'var(--hj-beige-ink-2)' }}>
          You've earned {owed} more reward{owed === 1 ? '' : 's'} — your code{owed === 1 ? '' : 's'} will arrive by email shortly.
        </p>
      )}

      {/* How it works */}
      <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--hj-beige-line)' }}>
        <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 10 }}>How it works</div>
        <ol className="m-0 flex flex-col gap-2.5" style={{ listStyle: 'none', padding: 0 }}>
          <li className="flex items-center gap-2.5" style={{ fontSize: 13.5, color: 'var(--hj-beige-ink-2)' }}>
            <span style={stepNum}>1</span>
            <span>
              Go to{' '}
              <a href={data.redeemUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--hj-deep-blue)', fontWeight: 600, textDecoration: 'none' }}>
                redeem.ticketrewards.com/splash
              </a>
            </span>
          </li>
          <li className="flex items-center gap-2.5" style={{ fontSize: 13.5, color: 'var(--hj-beige-ink-2)' }}>
            <span style={stepNum}>2</span>
            <span>Enter your code</span>
          </li>
          <li className="flex items-center gap-2.5" style={{ fontSize: 13.5, color: 'var(--hj-beige-ink-2)' }}>
            <span style={stepNum}>3</span>
            <span>Shop for movie &amp; theater tickets, live events and sports</span>
          </li>
        </ol>
        <p className="m-0" style={{ marginTop: 12, fontSize: 11.5, color: 'var(--hj-beige-ink-3)', fontStyle: 'italic' }}>
          *Minimum order of 2 tickets.{' '}
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--hj-deep-blue)', textDecoration: 'none', fontStyle: 'normal' }}>
            Read full terms and conditions here
          </a>.
        </p>
      </div>
    </section>
  );
};
